import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ageGroups, learningModules, mockActivities, mockProgress } from '../data/mockData';

const LearningHub = ({ language = 'farsi' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedAge, setSelectedAge] = useState(searchParams.get('age') || null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [progress] = useState(mockProgress);
  
  // Determine language from route if not passed as prop
  const currentLanguage = language || (location.pathname.includes('english') ? 'english' : 'farsi');

  useEffect(() => {
    const ageParam = searchParams.get('age');
    if (ageParam) {
      setSelectedAge(ageParam);
    }
  }, [searchParams]);

  const handleAgeSelect = (ageId) => {
    setSelectedAge(ageId);
    setSelectedModule(null);
    const hubPath = currentLanguage === 'english' ? '/dashboard/learning-hub-english' : '/dashboard/learning-hub-farsi';
    navigate(`${hubPath}?age=${ageId}`, { replace: true });
  };

  const handleModuleSelect = (moduleId) => {
    if (moduleId === 'vocabulary-sounds') {
      navigate(`/dashboard/vocabulary-categories?age=${selectedAge}&language=${currentLanguage}`);
    } else if (moduleId === 'storytelling') {
      navigate(`/dashboard/storytelling-video?age=${selectedAge}&language=${currentLanguage}`);
    } else {
      setSelectedModule(moduleId);
    }
  };

  const handleActivityClick = (activity) => {
    navigate(`/dashboard/activity/${activity.id}`, { state: { activity, ageGroup: selectedAge } });
  };

  const getCurrentAgeGroup = () => {
    return ageGroups.find(g => g.id === selectedAge);
  };

  const getCurrentActivities = () => {
    if (!selectedAge || !selectedModule) return [];
    return mockActivities[selectedAge]?.[selectedModule] || [];
  };

  const getAvailableModules = () => {
    if (!selectedAge) return [];
    return learningModules.filter(module => 
      module.availableFor.includes(selectedAge)
    );
  };

  const isSimpleUI = () => {
    return selectedAge === '6-8';
  };

  const getModuleColor = (moduleId) => {
    const cuteColors = {
      'vocabulary-sounds': '#9B59B6',      // Vibrant purple for Vocabulary
      'listening-speaking': '#E91E63',     // Bright pink for Games
      'storytelling': '#00BCD4',            // Bright cyan for Storytelling
      'conversation': '#FF9800',           // Warm orange for Conversation
      'reading-comprehension': '#4CAF50'   // Green for Reading (if needed)
    };
    return cuteColors[moduleId] || '#DD2E44';
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      fontFamily: "'Nunito', sans-serif",
      backgroundColor: '#F4ABBA',
      color: '#333',
      paddingTop: '80px'
    }}>
      {/* Progress Banner */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#DD2E44', marginBottom: '5px' }}>
              Your Progress
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {progress.completedActivities} / {progress.totalActivities} activities completed
            </div>
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#DD2E44' }}>
                  ⭐ {progress.totalStars}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Stars</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#DD2E44' }}>
                  🔥 {progress.currentStreak}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Day Streak</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#DD2E44' }}>
                  🏆 {progress.badges.length}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Badges</div>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard/avatar-creator')}
              style={{
                border: 'none',
                borderRadius: '999px',
                padding: '12px 24px',
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: '700',
                fontSize: 'clamp(18px, 2vw, 24px)',
                color: '#ffffff',
                background: 'linear-gradient(135deg, #5AFF5C 0%, #37eb39 55%, #1FA521 100%)',
                boxShadow: '0 8px 20px rgba(55, 235, 57, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 25px rgba(55, 235, 57, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 20px rgba(55, 235, 57, 0.3)';
              }}
            >
              🎨 Create Avatar
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Age Group Selection */}
        <section style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: selectedAge ? '1' : 'none' }}>
              <h2 style={{
                fontSize: isSimpleUI() ? 'clamp(36px, 4vw, 48px)' : 'clamp(42px, 5vw, 56px)',
                fontWeight: '700',
                fontFamily: "'Fredoka', sans-serif",
                color: '#DD2E44',
                margin: 0,
                textAlign: selectedAge ? 'left' : 'center'
              }}>
                {!selectedAge ? 'Select Your Age Group' : `Group ${selectedAge === '6-8' ? 'A' : 'B'}: ${getCurrentAgeGroup()?.name}`}
              </h2>
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#666',
                backgroundColor: '#FFF5F7',
                padding: '8px 16px',
                borderRadius: '20px',
                border: '2px solid #DD2E44',
                fontFamily: "'Fredoka', sans-serif"
              }}>
                {currentLanguage === 'english' ? '🇬🇧 English' : '🇮🇷 Farsi'}
              </span>
            </div>
            {selectedAge && (
              <button
                onClick={() => {
                  setSelectedAge(null);
                  setSelectedModule(null);
                  const hubPath = currentLanguage === 'english' ? '/dashboard/learning-hub-english' : '/dashboard/learning-hub-farsi';
                  navigate(hubPath);
                }}
                style={{
                  backgroundColor: '#FFF5F7',
                  color: '#DD2E44',
                  padding: '10px 20px',
                  borderRadius: '15px',
                  border: '2px solid #DD2E44',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Change Age Group
              </button>
            )}
          </div>
          
          {!selectedAge && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {ageGroups.map((group) => {
                const ages = group.id === '6-8' ? [6, 7, 8] : [9, 10, 11];
                return (
                  <div key={group.id} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    perspective: '1000px'
                  }}>
                    <button
                      onClick={() => handleAgeSelect(group.id)}
                      style={{
                        backgroundColor: 'white',
                        color: '#DD2E44',
                        padding: '0',
                        borderRadius: '25px',
                        border: '3px solid #DD2E44',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        boxShadow: '0 4px 15px rgba(221, 46, 68, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        width: '100%',
                        aspectRatio: '4 / 3',
                        transformStyle: 'preserve-3d'
                      }}
                      onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        const centerX = rect.width / 2;
                        const centerY = rect.height / 2;
                        const rotateX = (y - centerY) / 25;
                        const rotateY = (centerX - x) / 25;
                        e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-3px) scale(1.01)`;
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(221, 46, 68, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(221, 46, 68, 0.15)';
                      }}
                    >
                      {group.id === '6-8' ? (
                        <img
                          src="/kids-age-6-to-8.png"
                          alt="Kids age 6 to 8"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <img
                          src="/kids-age-9-to-11.png"
                          alt="Kids age 9 to 11"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                    </button>
                    <div style={{
                      marginTop: '25px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#DD2E44',
                        marginBottom: '6px'
                      }}>
                        Ages {ages.join(' ')}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#666',
                        marginTop: '4px'
                      }}>
                        {group.name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Learning Modules */}
        {selectedAge && (
          <>
            <section style={{ marginBottom: '40px' }}>
              <h2 style={{
                fontSize: isSimpleUI() ? '24px' : '32px',
                fontWeight: '800',
                color: '#DD2E44',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                {isSimpleUI() ? '🎮 Choose a Game' : 'Choose a Learning Module'}
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {getAvailableModules().map((module) => (
                  <div
                    key={module.id}
                    onClick={() => handleModuleSelect(module.id)}
                    style={{
                      backgroundColor: selectedModule === module.id ? '#FFF5F7' : 'white',
                      padding: isSimpleUI() ? '25px' : '30px',
                      borderRadius: '20px',
                      border: '3px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(221, 46, 68, 0.15)',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '200px',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(221, 46, 68, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(221, 46, 68, 0.15)';
                    }}
                  >
                    <div style={{ 
                      minHeight: isSimpleUI() ? '160px' : '180px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      marginBottom: '15px',
                      width: '100%'
                    }}>
                      {selectedAge === '6-8' && module.id === 'vocabulary-sounds' ? (
                        <img
                          src="/abc-2.png"
                          alt="ABC"
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            maxHeight: isSimpleUI() ? '160px' : '180px',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                      ) : selectedAge === '6-8' && module.id === 'listening-speaking' ? (
                        <img
                          src="/kids-videeos.png"
                          alt="Games"
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            maxHeight: isSimpleUI() ? '160px' : '180px',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                      ) : selectedAge === '6-8' && module.id === 'storytelling' ? (
                        <img
                          src="/book.png"
                          alt="Storytelling"
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            maxHeight: isSimpleUI() ? '160px' : '180px',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                      ) : selectedAge === '6-8' && module.id === 'conversation' ? (
                        <img
                          src="/conversation.png"
                          alt="Conversation"
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            maxHeight: isSimpleUI() ? '160px' : '180px',
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: isSimpleUI() ? '40px' : '48px' }}>
                          {module.icon}
                        </div>
                      )}
                    </div>
                    <h3 style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: getModuleColor(module.id),
                      marginBottom: isSimpleUI() ? '5px' : '10px',
                      fontFamily: "'Nunito', sans-serif"
                    }}>
                      {selectedAge === '6-8' && module.id === 'listening-speaking' 
                        ? 'Games' 
                        : (isSimpleUI() ? module.name.split(' ')[0] : module.name)
                      }
                    </h3>
                    {!isSimpleUI() && (
                      <p style={{ fontSize: '14px', color: '#666' }}>
                        {module.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Activities List */}
            {selectedModule && (
              <section>
                <h2 style={{
                  fontSize: isSimpleUI() ? '24px' : '32px',
                  fontWeight: '800',
                  color: '#DD2E44',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  {isSimpleUI() ? '🎯 Activities' : `Activities for ${getCurrentAgeGroup()?.label}`}
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '20px'
                }}>
                  {getCurrentActivities().map((activity) => (
                    <div
                      key={activity.id}
                      onClick={() => handleActivityClick(activity)}
                      style={{
                        backgroundColor: 'white',
                        padding: isSimpleUI() ? '20px' : '25px',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(221, 46, 68, 0.15)',
                        border: '2px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(221, 46, 68, 0.3)';
                        e.currentTarget.style.borderColor = '#DD2E44';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(221, 46, 68, 0.15)';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      <div style={{
                        fontSize: isSimpleUI() ? '32px' : '36px',
                        marginBottom: '15px',
                        textAlign: 'center'
                      }}>
                        {activity.type === 'flashcards' && '🎴'}
                        {activity.type === 'matching' && '🔗'}
                        {activity.type === 'simon-says' && '👋'}
                        {activity.type === 'bingo' && '🎲'}
                        {activity.type === 'songs' && '🎵'}
                        {activity.type === 'story' && '📖'}
                        {activity.type === 'story-cubes' && '🎲'}
                        {activity.type === 'picture-story' && '🖼️'}
                        {activity.type === 'telephone' && '📞'}
                        {activity.type === 'small-talk' && '💬'}
                        {activity.type === 'read-act' && '📚'}
                        {!['flashcards', 'matching', 'simon-says', 'bingo', 'songs', 'story', 'story-cubes', 'picture-story', 'telephone', 'small-talk', 'read-act'].includes(activity.type) && '🎮'}
                      </div>
                      <h3 style={{
                        fontSize: isSimpleUI() ? '18px' : '20px',
                        fontWeight: '700',
                        color: '#DD2E44',
                        marginBottom: isSimpleUI() ? '8px' : '10px',
                        textAlign: 'center'
                      }}>
                        {activity.name}
                      </h3>
                      {!isSimpleUI() && activity.description && (
                        <p style={{
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '10px',
                          textAlign: 'center'
                        }}>
                          {activity.description}
                        </p>
                      )}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '14px',
                        color: '#666',
                        marginTop: '15px',
                        alignItems: 'center'
                      }}>
                        <span>⏱️ {activity.duration} min</span>
                        <span style={{
                          backgroundColor: activity.difficulty === 'easy' ? '#A8E6CF' : '#FFE66D',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {activity.difficulty}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

          </>
        )}

        {/* Back Button */}
        <div style={{ marginTop: '20px', marginBottom: '40px', textAlign: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              border: 'none',
              borderRadius: '999px',
              padding: '10px 20px',
              minWidth: '120px',
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: '700',
              fontSize: 'clamp(20px, 2.5vw, 28px)',
              lineHeight: '1',
              color: '#ffffff',
              letterSpacing: '0.02em',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #5AFF5C 0%, #37eb39 55%, #1FA521 100%)',
              boxShadow: '0 10px 20px rgba(55, 235, 57, 0.3), 0 3px 8px rgba(0, 0, 0, 0.12)',
              transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(1px)';
              e.target.style.boxShadow = '0 8px 16px rgba(55, 235, 57, 0.3), 0 2px 6px rgba(0, 0, 0, 0.12)';
              e.target.style.filter = 'saturate(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 20px rgba(55, 235, 57, 0.3), 0 3px 8px rgba(0, 0, 0, 0.12)';
              e.target.style.filter = 'none';
            }}
            onMouseDown={(e) => {
              e.target.style.transform = 'translateY(2px)';
              e.target.style.boxShadow = '0 4px 10px rgba(55, 235, 57, 0.22), 0 1px 4px rgba(0, 0, 0, 0.12)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = 'translateY(1px)';
              e.target.style.boxShadow = '0 8px 16px rgba(55, 235, 57, 0.3), 0 2px 6px rgba(0, 0, 0, 0.12)';
            }}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearningHub;